import {
  logEvent,
  setConsent as firebaseSetConsent,
  setUserId as firebaseSetUserId
} from "@react-native-firebase/analytics";
import * as AmplitudeNative from "@amplitude/analytics-react-native";
import uuidv4 from "uuid";

import { UrlProvider } from "../../api/UrlProvider";
import { getIsAndroid } from "../platformHelper";
import getPageVisitEventData from "../Amplitude/getPageVisitEventData";
import config from "../../config";
import {
  FIREBASE_ANALYTICS_CALL_TYPE,
  FirebaseAnalyticsCallQueue
} from "./FirebaseAnalyticsCallQueue";

// RN Firebase validates per-key types (see namespaced.js setConsent).
const FIREBASE_CONSENT_STRING_KEYS = new Set(["security_storage"]);

export class AnalyticsEventTrackerNative {
  canTrackUserData = false;
  analytics = null;
  pendingConsentSettings = null;
  consentCompleted = false;

  constructor() {
    this.callQueue = new FirebaseAnalyticsCallQueue();
  }

  setAnalytics(analytics) {
    this.analytics = analytics;

    if (this.pendingConsentSettings) {
      const pendingSettings = this.pendingConsentSettings;
      this.pendingConsentSettings = null;
      void this.setConsent(pendingSettings).then(applied => {
        if (applied !== false) {
          return this.markConsentCompleted();
        }
      });
    }
  }

  async setConsent(settings) {
    const normalizedSettings = Object.fromEntries(
      Object.entries(settings || {}).map(([key, value]) => {
        const granted = value === "granted" || value === true;

        if (FIREBASE_CONSENT_STRING_KEYS.has(key)) {
          return [key, granted ? "granted" : "denied"];
        }

        return [key, granted];
      })
    );

    if (!this.analytics) {
      this.pendingConsentSettings = normalizedSettings;
      return false;
    }

    await firebaseSetConsent(this.analytics, normalizedSettings);
    return true;
  }

  async ensureQueueHydrated() {
    await this.callQueue.read();
  }

  async markConsentCompleted() {
    this.consentCompleted = true;
    const calls = await this.callQueue.read();

    for (const call of calls) {
      await this._dispatchFirebaseCall(call);
    }

    await this.callQueue.clear();
  }

  async setFirebaseUserId(userId) {
    await this._enqueueOrSend({
      type: FIREBASE_ANALYTICS_CALL_TYPE.SET_USER_ID,
      userId
    });
  }

  async _enqueueOrSend(call) {
    if (this.consentCompleted) {
      await this._dispatchFirebaseCall(call);
      return;
    }

    await this.callQueue.add(call);
  }

  async _dispatchFirebaseCall(call) {
    if (call?.type === FIREBASE_ANALYTICS_CALL_TYPE.SET_USER_ID) {
      await this._sendFirebaseUserId(call.userId);
      return;
    }

    if (call?.type === FIREBASE_ANALYTICS_CALL_TYPE.LOG_EVENT) {
      await this._sendFirebaseEvent(call.name, call.params);
    }
  }

  async trackUnableToSetUpNotifications(errorString) {
    try {
      await this._trackFirebaseEvent("UnableToSetUpNotifications", {
        errorString: errorString.slice(0, 200)
      });
    } catch (e) {}
  }

  async trackRegisterConversion(registerSource, userId) {
    if (config.trackEvents) {
      try {
        const params = { user_id: userId };

        if (registerSource) {
          params.source = registerSource;
        }

        await this._trackFirebaseEvent("Registration", params);
      } catch (e) {}

      await this._trackFacebookEvent(
        "fb_mobile_complete_registration",
        registerSource
      );
    }

    await AmplitudeNative.setUserId(String(userId));
  }

  async _trackFacebookEvent(event, source) {
    if (config.facebookCredentials) {
      const customEvents = [
        {
          _eventName: event,
          fb_registration_method: source,
          fb_currency: "PLN",
          fb_system: getIsAndroid() ? "Android" : "iOS",
          _valueToSum: 0
        }
      ];

      const url = UrlProvider.getUrl(
        "fbTracker.trackEvent",
        {
          appId: config.facebookCredentials.appId,
          clientToken: config.facebookCredentials.clientToken,
          event: "CUSTOM_APP_EVENTS",
          customEvents: JSON.stringify(customEvents),
          applicationTrackingEnabled: 0,
          advertiserTrackingEnabled: 0,
          advertiserId: uuidv4()
        },
        ""
      ).replace(/\s/gm, "");

      return await fetch(url, {
        method: "POST"
      });
    }
  }

  async _trackFirebaseEvent(name, params) {
    await this._enqueueOrSend({
      type: FIREBASE_ANALYTICS_CALL_TYPE.LOG_EVENT,
      name,
      params
    });
  }

  async _sendFirebaseEvent(name, params) {
    if (!logEvent || !this.analytics) {
      throw new Error("Firebase analytics is not ready");
    }

    await logEvent(this.analytics, name, params);
  }

  async _sendFirebaseUserId(userId) {
    if (!firebaseSetUserId || !this.analytics) {
      throw new Error("Firebase analytics is not ready");
    }

    await firebaseSetUserId(this.analytics, userId);
  }

  async trackUserLogInEvent() {
    if (!this.canTrackUserData) {
      return false;
    }

    await AmplitudeNative.track("log_in");
  }

  async trackOfferModalEvent(id = null) {
    if (!this.canTrackUserData) {
      return false;
    }

    const type = id === null ? "premium_modal" : "marketing_message";

    await AmplitudeNative.track("offer_modal", {
      marketing_message_id: id,
      type
    });
  }

  async trackStockPageSectionShownEvent(sectionName, market, ticker) {
    if (!this.canTrackUserData) {
      return false;
    }

    await AmplitudeNative.track("stock_section_show", {
      section_name: sectionName,
      market,
      ticker
    });
  }

  async trackSearchListStockOnClickEvent({
    searchListName,
    market,
    ticker,
    searchListPosition
  }) {
    if (!this.canTrackUserData) {
      return false;
    }

    await AmplitudeNative.track("search_list_stock_click", {
      search_list_name: searchListName,
      market,
      ticker,
      position: searchListPosition
    });
  }

  async trackSearchResultOnClickEvent({ market, ticker }) {
    if (!this.canTrackUserData) {
      return false;
    }

    await AmplitudeNative.track("user_search_click", {
      market,
      ticker
    });
  }

  async trackSearchPageShownEvent() {
    if (!this.canTrackUserData) {
      return false;
    }

    await AmplitudeNative.track("search_click");
  }

  async trackRegistrationModalEvent() {
    if (!this.canTrackUserData) {
      return false;
    }

    await AmplitudeNative.track("registration_modal");
  }

  async trackPortfolioCreatedEvent() {
    if (!this.canTrackUserData) {
      return false;
    }

    await AmplitudeNative.track("portfolio_created");
  }

  async trackPortfolioTransactionEvent() {
    if (!this.canTrackUserData) {
      return false;
    }

    await AmplitudeNative.track("portfolio_transaction");
  }

  async trackStockAlertOnEvent(type, market, name) {
    if (!this.canTrackUserData) {
      return false;
    }

    await AmplitudeNative.track("stock_alert_on", {
      type,
      market,
      name
    });
  }

  async trackTagCreatedEvent() {
    if (!this.canTrackUserData) {
      return false;
    }

    await AmplitudeNative.track("tag_created");
  }

  async trackScreenView(scene, sceneUrl) {
    if (sceneUrl) {
      await this._trackFirebaseEvent("screen_view", { screen_name: sceneUrl });
    }

    if (scene) {
      const amplitudeEventData = {
        ...getPageVisitEventData(sceneUrl),
        url: sceneUrl,
        page: scene
      };

      await AmplitudeNative.track("page_visit", amplitudeEventData);
    }
  }

  async trackAppUpdatePromptShown({ platform, installedVersion, latestVersion }) {
    if (!this.canTrackUserData) {
      return false;
    }

    await AmplitudeNative.track("app_update_prompt_shown", {
      platform,
      installed_version: installedVersion,
      latest_version: latestVersion
    });
  }

  async trackAppUpdatePromptDismissed({
    platform,
    installedVersion,
    latestVersion
  }) {
    if (!this.canTrackUserData) {
      return false;
    }

    await AmplitudeNative.track("app_update_prompt_dismissed", {
      platform,
      installed_version: installedVersion,
      latest_version: latestVersion
    });
  }

  async trackAppUpdateForceShown({ platform, installedVersion, latestVersion }) {
    if (!this.canTrackUserData) {
      return false;
    }

    await AmplitudeNative.track("app_update_force_shown", {
      platform,
      installed_version: installedVersion,
      latest_version: latestVersion
    });
  }

  async trackAppUpdateStoreOpened({ platform, installedVersion, latestVersion }) {
    if (!this.canTrackUserData) {
      return false;
    }

    await AmplitudeNative.track("app_update_store_opened", {
      platform,
      installed_version: installedVersion,
      latest_version: latestVersion
    });
  }
}

export default new AnalyticsEventTrackerNative();
